import { useEffect, useState } from "react";
import { Building2, CalendarDays } from "lucide-react-native";
import { ScrollView, View } from "react-native";
import {
  ActionBanner,
  Dialog,
  DialogActions,
  DialogHeader,
  FieldMessage,
  PrimaryButton,
  DateField,
  SecondaryButton,
  TextField,
} from "@louise/moon-ui";
import { useUpdateCycle } from "@src/packages/cycles/hooks/useCycles";
import { useTranslations } from "@src/packages/i18n";

type Props = {
  visible: boolean;
  onClose: () => void;
  cycleId: string;
  initialClinic: string | null | undefined;
  initialStartDate: string | null | undefined;
  initialEndDate: string | null | undefined;
};

export default function EditPastCycleDetailsDialog({
  visible,
  onClose,
  cycleId,
  initialClinic,
  initialStartDate,
  initialEndDate,
}: Props) {
  const t = useTranslations();
  const updateCycle = useUpdateCycle();

  const [clinic, setClinic] = useState(initialClinic ?? "");
  const [startDate, setStartDate] = useState(initialStartDate ?? "");
  const [endDate, setEndDate] = useState(initialEndDate ?? "");
  const [formError, setFormError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setClinic(initialClinic ?? "");
    setStartDate(initialStartDate ?? "");
    setEndDate(initialEndDate ?? "");
    setFormError(null);
    setSaveError(null);
  }, [visible, initialClinic, initialStartDate, initialEndDate]);

  const isSaving = updateCycle.isPending;

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handleSave = async () => {
    setFormError(null);
    setSaveError(null);

    if (endDate && startDate && endDate < startDate) {
      setFormError(t("care.all_past_cycles.validation_dates"));
      return;
    }

    const trimmedClinic = clinic.trim();
    const nextClinic = trimmedClinic.length > 0 ? trimmedClinic : null;
    const nextStart = startDate || null;
    const nextEnd = endDate || null;

    const clinicChanged = nextClinic !== (initialClinic ?? null);
    const startChanged = nextStart !== (initialStartDate ?? null);
    const endChanged = nextEnd !== (initialEndDate ?? null);

    if (!clinicChanged && !startChanged && !endChanged) {
      onClose();
      return;
    }

    try {
      await updateCycle.mutateAsync({
        cycleId,
        data: {
          ...(clinicChanged ? { provider: nextClinic } : {}),
          ...(startChanged ? { startDate: nextStart } : {}),
          ...(endChanged ? { endDate: nextEnd } : {}),
        },
      });
      onClose();
    } catch {
      setSaveError(t("care.past_cycle_detail.save_details_error"));
    }
  };

  return (
    <Dialog
      size="wide"
      visible={visible}
      onClose={handleClose}
      layout="centered"
      closeButton="minimal"
      dismissOnBackdropTap={!isSaving}
    >
      <DialogHeader
        title={t("care.past_cycle_detail.edit_details_title")}
        subtitle={t("care.past_cycle_detail.edit_details_subtitle")}
        style={{ marginTop: 4, marginBottom: 18 }}
      />

      <ScrollView
        style={{ maxHeight: 380 }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <TextField
          label={t("care.all_past_cycles.clinic_label")}
          labelIcon={Building2}
          value={clinic}
          onChangeText={setClinic}
          placeholder={t("care.all_past_cycles.clinic_placeholder")}
          editable={!isSaving}
        />

        <View style={{ flexDirection: "row", gap: 10, marginTop: 16 }}>
          <View style={{ flex: 1 }}>
            <DateField
              label={t("care.all_past_cycles.start_date_label")}
              labelIcon={CalendarDays}
              value={startDate}
              onChange={setStartDate}
              disabled={isSaving}
            />
          </View>
          <View style={{ flex: 1 }}>
            <DateField
              label={t("care.all_past_cycles.end_date_label")}
              labelIcon={CalendarDays}
              value={endDate}
              onChange={setEndDate}
              disabled={isSaving}
            />
          </View>
        </View>

        {formError ? (
          <FieldMessage style={{ marginTop: 12 }}>{formError}</FieldMessage>
        ) : null}
        {saveError ? (
          <ActionBanner
            variant="important"
            message={saveError}
            style={{ marginTop: 12 }}
          />
        ) : null}
      </ScrollView>

      <DialogActions style={{ marginTop: 18 }}>
        <SecondaryButton
          label={t("care.all_past_cycles.cancel_button")}
          onPress={handleClose}
          disabled={isSaving}
          flex={1}
        />
        <PrimaryButton
          label={t("care.past_cycle_detail.save_details_button")}
          onPress={handleSave}
          loading={isSaving}
          flex={1}
        />
      </DialogActions>
    </Dialog>
  );
}
